import { Culler } from "pixi.js";
import type { CustomViewport } from "../CustomViewport";

// Renderer screen-space overscan prevents rAF-coalesced culling from popping at viewport edges.
const CULL_OVERSCAN_PX = 150;

export default class NativeCullingManager {
    private viewport: CustomViewport | null = null;
    private frameId: number | null = null;
    private readonly onViewportChange: () => void;

    constructor() {
        this.onViewportChange = this.requestCull.bind(this);
    }

    connect(viewport: CustomViewport): void {
        if (this.viewport === viewport) {
            this.requestCull();
            return;
        }

        if (this.viewport) this.disconnect(this.viewport);

        this.viewport = viewport;

        viewport.on("moved", this.onViewportChange);
        viewport.on("zoomed", this.onViewportChange);
        viewport.on("resized", this.onViewportChange);
        this.requestCull();
    }

    requestCull(): void {
        if (this.frameId !== null) return;

        this.frameId = requestAnimationFrame(() => {
            this.frameId = null;
            this.cull();
        });
    }

    private cull(): void {
        const viewport = this.viewport;
        if (!viewport?.isUsable) return;

        // The view is in renderer screen coordinates. Pixi transforms each object's local scene-space
        // cullArea through the viewport before testing it against this rectangle.
        Culler.shared.cull(
            viewport,
            {
                x: -CULL_OVERSCAN_PX,
                y: -CULL_OVERSCAN_PX,
                width: viewport.screenWidth + 2 * CULL_OVERSCAN_PX,
                height: viewport.screenHeight + 2 * CULL_OVERSCAN_PX,
            },
            false,
        );
    }

    disconnect(viewport: CustomViewport): void {
        if (this.viewport !== viewport) return;

        viewport.off("moved", this.onViewportChange);
        viewport.off("zoomed", this.onViewportChange);
        viewport.off("resized", this.onViewportChange);

        if (this.frameId !== null) cancelAnimationFrame(this.frameId);
        this.frameId = null;
        this.viewport = null;
    }
}
